import { RouteStateConfig, StorageStrategy, createRouteConfig } from '../models/route-config.model';

/**
 * Central configuration for routes that should have state preserved
 *
 * To add a new route for state preservation:
 * 1. Add a new entry to PRESERVED_ROUTES array
 * 2. Implement StatefulComponent interface in your component
 * 3. That's it! The framework handles the rest automatically
 *
 * @example
 * ```typescript
 * createRouteConfig({
 *   path: '/my-route',
 *   name: 'my-route',
 *   cacheDuration: 600000, // 10 minutes
 *   stateKeys: ['filters', 'pagination'], // optional: specific keys only
 * })
 * ```
 */
export const PRESERVED_ROUTES: RouteStateConfig[] = [
  // Order Review - Preserve table state, filters, and selections
  createRouteConfig({
    path: 'review',
    name: 'order-review',
    stateKeys: ['tableState', 'filters', 'selectedRow', 'selectedIndex', 'activeTab', 'dateRange'],
    cacheDuration: 600000, // 10 minutes
    storageStrategy: StorageStrategy.SESSION,
    persistAcrossRefresh: true,
    preserveScrollPosition: true,
    priority: 10,
  }),

  // Customer Dashboard - Preserve active tab and filters
  createRouteConfig({
    path: 'customers/customer-dashboard',
    name: 'customer-dashboard',
    stateKeys: ['activeTab', 'filters', 'isNavigationCollapsed'],
    cacheDuration: 600000, // 10 minutes
    storageStrategy: StorageStrategy.SESSION,
    persistAcrossRefresh: true,
    preserveScrollPosition: false, // Customer dashboard has internal scrolling
    priority: 10,
  }),

  // Ticket System - Preserve kanban state and filters
  createRouteConfig({
    path: 'ticket-system',
    name: 'ticket-system',
    stateKeys: ['kanbanState', 'filters', 'selectedTicket', 'viewMode'],
    cacheDuration: 300000, // 5 minutes
    storageStrategy: StorageStrategy.SESSION,
    persistAcrossRefresh: false,
    preserveScrollPosition: true,
    priority: 8,
  }),

  // Bulk Operations - Preserve selections and configurations
  createRouteConfig({
    path: 'bulk-process',
    name: 'bulk-operations',
    stateKeys: ['selectedItems', 'bulkAction', 'filters'],
    cacheDuration: 900000, // 15 minutes
    storageStrategy: StorageStrategy.SESSION,
    persistAcrossRefresh: true,
    preserveScrollPosition: true,
    priority: 7,
  }),

  // Payments/Transactions - Preserve filters and date ranges
  createRouteConfig({
    path: 'transaction',
    name: 'payments',
    stateKeys: ['filters', 'dateRange', 'selectedTransaction'],
    cacheDuration: 600000, // 10 minutes
    storageStrategy: StorageStrategy.SESSION,
    persistAcrossRefresh: true,
    preserveScrollPosition: true,
    priority: 6,
  }),

  // Reports - Preserve report parameters and results
  createRouteConfig({
    path: 'new-reports',
    name: 'reports',
    stateKeys: ['reportType', 'filters', 'dateRange', 'generatedReport'],
    cacheDuration: 1200000, // 20 minutes
    storageStrategy: StorageStrategy.LOCAL,
    persistAcrossRefresh: true,
    preserveScrollPosition: true,
    priority: 5,
  }),

  // Porting - Preserve porting request state
  createRouteConfig({
    path: 'porting',
    name: 'porting',
    stateKeys: ['filters', 'selectedRequest', 'activeTab'],
    cacheDuration: 900000, // 15 minutes
    storageStrategy: StorageStrategy.SESSION,
    persistAcrossRefresh: true,
    preserveScrollPosition: true,
    priority: 6,
  }),

  // Search Results - Preserve search criteria and results
  createRouteConfig({
    path: 'search-result',
    name: 'search-results',
    stateKeys: ['searchTerm', 'filters', 'results', 'selectedResult'],
    cacheDuration: 300000, // 5 minutes
    storageStrategy: StorageStrategy.MEMORY_ONLY,
    persistAcrossRefresh: false,
    preserveScrollPosition: true,
    priority: 3,
  }),

  // Awaiting Shipment - Preserve order selections and filters
  createRouteConfig({
    path: 'awaiting-shipment',
    name: 'awaiting-shipment',
    stateKeys: ['filters', 'selectedOrders', 'groupingMode'],
    cacheDuration: 600000, // 10 minutes
    storageStrategy: StorageStrategy.SESSION,
    persistAcrossRefresh: true,
    preserveScrollPosition: true,
    priority: 7,
  }),

  // Dropship Orders - Preserve order state
  createRouteConfig({
    path: 'fullfillment',
    name: 'dropship-orders',
    stateKeys: ['filters', 'selectedOrders', 'statusFilter'],
    cacheDuration: 600000, // 10 minutes
    storageStrategy: StorageStrategy.SESSION,
    persistAcrossRefresh: true,
    preserveScrollPosition: true,
    priority: 7,
  }),

  // Customer Email (Mandrill) - Fully automatic state management with property mapping
  createRouteConfig({
    path: 'customer-email',
    name: 'customer-email',
    cacheDuration: 600000, // 10 minutes
    storageStrategy: StorageStrategy.SESSION,
    persistAcrossRefresh: false,
    preserveScrollPosition: false, // Disable scroll position since table handles its own pagination
    priority: 8,
    // Automatic property mapping - maps component properties to state keys
    // No need for getState/setState methods!
    propertyMap: {
      'emailList': 'emailList',           // component.emailList() -> state.emailList
      'filters': 'filters',                // component.filters -> state.filters
      'isSkeletonLoader': 'isSkeletonLoader', // component.isSkeletonLoader() -> state.isSkeletonLoader
      'highlightedRowIndex': 'highlightedRowIndex', // component.highlightedRowIndex -> state.highlightedRowIndex
      'initialDataLoaded': 'initialDataLoaded' // component.initialDataLoaded -> state.initialDataLoaded
      // Note: tableConfig is NOT included - TanStack Table handles its own pagination state via persistState: true
    },
    // Automatic post-restoration actions
    postRestoration: {
      highlightRow: {
        indexProperty: 'highlightedRowIndex',
        rowSelector: '.table-row',
        highlightClass: 'highlighted-row',
        scrollIntoView: false, // Disable scroll to prevent page jumping - pagination handles visibility
        // Enable automatic pagination restoration - StateSnapshot will navigate to correct page
        restorePagination: true,
        pageSize: 10 // Must match table's pageSize configuration
      }
    }
  }),

  // Plan Renewals - Preserve table data, pagination, filters, and KPI selection
  createRouteConfig({
    path: 'renewal-reports/renewals/plan-renewals',
    name: 'plan-renewals',
    cacheDuration: 600000, // 10 minutes
    storageStrategy: StorageStrategy.SESSION,
    persistAcrossRefresh: false,
    preserveScrollPosition: false, // Table handles its own pagination
    priority: 8,
    propertyMap: {
      'rowDatas': 'rowDatas',                     // Table data
      'selectedKpiCard': 'selectedKpiCard',       // Selected KPI card
      'currentPage': 'currentPage',               // Current page number
      'itemsPerPage': 'itemsPerPage',             // Items per page
      'totalOrders': 'totalOrders',               // Total count
      'days': 'days',                             // Date filter: days
      'fromDate': 'fromDate',                     // Date filter: from
      'toDate': 'toDate',                         // Date filter: to
      'customerStatus': 'customerStatus',         // Customer status filters
      'subscriptionStatus': 'subscriptionStatus', // Subscription status filters
      'poAction': 'poAction',                     // PO action filters
      'searchTerm': 'searchTerm',                 // Search term
      'highlightedRowIndex': 'highlightedRowIndex', // Highlighted row
      'isSkeletonLoader': 'isSkeletonLoader'      // Loading state
    },
    postRestoration: {
      highlightRow: {
        indexProperty: 'highlightedRowIndex',
        rowSelector: '.table-row',
        highlightClass: 'highlighted-row',
        scrollIntoView: false,
        restorePagination: false // Pagination is stored directly in state
      }
    }
  }),

  // Renewal Corrections (Mis-Aligned Subscriptions) - Preserve table data, pagination, and filters
  createRouteConfig({
    path: 'renewal-reports/renewals/renewal-corrections',
    name: 'renewal-corrections',
    cacheDuration: 600000, // 10 minutes
    storageStrategy: StorageStrategy.SESSION,
    persistAcrossRefresh: false,
    preserveScrollPosition: false, // Table handles its own pagination
    priority: 8,
    propertyMap: {
      'rowDatas': 'rowDatas',                     // Table data
      'currentPage': 'currentPage',               // Current page number
      'itemsPerPage': 'itemsPerPage',             // Items per page
      'totalOrders': 'totalOrders',               // Total count
      'riskLevel': 'riskLevel',                   // Risk level filter
      'subscriptionStatus': 'subscriptionStatus', // Subscription status filter
      'customerStatus': 'customerStatus',         // Customer status filter
      'searchTerm': 'searchTerm',                 // Search term
      'highlightedRowIndex': 'highlightedRowIndex', // Highlighted row
      'isSkeletonLoader': 'isSkeletonLoader'      // Loading state
      // Note: advanceDateFilter is managed by RenewalReportsStateService and doesn't need to be stored here
    },
    postRestoration: {
      highlightRow: {
        indexProperty: 'highlightedRowIndex',
        rowSelector: '.table-row',
        highlightClass: 'highlighted-row',
        scrollIntoView: false,
        restorePagination: false // Pagination is stored directly in state
      }
    }
  }),

  // Plan Change - Preserve table data, pagination, filters, and date range
  createRouteConfig({
    path: 'renewal-reports/plan-change',
    name: 'plan-change',
    cacheDuration: 600000, // 10 minutes
    storageStrategy: StorageStrategy.SESSION,
    persistAcrossRefresh: false,
    preserveScrollPosition: false, // Table handles its own pagination
    priority: 8,
    propertyMap: {
      'rowDatas': 'rowDatas',                     // Table data
      'currentPage': 'currentPage',               // Current page number
      'itemsPerPage': 'itemsPerPage',             // Items per page
      'totalItems': 'totalItems',                 // Total count
      'days': 'days',                             // Date filter: days
      'fromDate': 'fromDate',                     // Date filter: from
      'toDate': 'toDate',                         // Date filter: to
      'statusFilter': 'statusFilter',             // Status filter
      'searchTerm': 'searchTerm',                 // Search term
      'highlightedRowIndex': 'highlightedRowIndex', // Highlighted row
      'isSkeletonLoader': 'isSkeletonLoader'      // Loading state
    },
    postRestoration: {
      highlightRow: {
        indexProperty: 'highlightedRowIndex',
        rowSelector: '.table-row',
        highlightClass: 'highlighted-row',
        scrollIntoView: false,
        restorePagination: false // Pagination is stored directly in state
      }
    }
  }),

  // Scheduled Downgrades - Preserve table data, pagination, and filters
  createRouteConfig({
    path: 'renewal-reports/scheduled-downgrades',
    name: 'scheduled-downgrades',
    cacheDuration: 600000, // 10 minutes
    storageStrategy: StorageStrategy.SESSION,
    persistAcrossRefresh: false,
    preserveScrollPosition: false, // Table handles its own pagination
    priority: 8,
    propertyMap: {
      'rowDatas': 'rowDatas',                     // Table data
      'currentPage': 'currentPage',               // Current page number
      'itemsPerPage': 'itemsPerPage',             // Items per page
      'totalOrders': 'totalOrders',               // Total count
      'selectedStatus': 'selectedStatus',         // Status filter
      'searchTerm': 'searchTerm',                 // Search term
      'highlightedRowIndex': 'highlightedRowIndex', // Highlighted row
      'isSkeletonLoader': 'isSkeletonLoader'      // Loading state
    },
    postRestoration: {
      highlightRow: {
        indexProperty: 'highlightedRowIndex',
        rowSelector: '.table-row',
        highlightClass: 'highlighted-row',
        scrollIntoView: false,
        restorePagination: false // Pagination is stored directly in state
      }
    }
  }),

  // Subscription List - Preserve table data, pagination, filters, and search
  createRouteConfig({
    path: 'subscriptions',
    name: 'subscription-list',
    cacheDuration: 600000, // 10 minutes
    storageStrategy: StorageStrategy.SESSION,
    persistAcrossRefresh: false,
    preserveScrollPosition: false, // Table handles its own pagination
    priority: 8,
    propertyMap: {
      'rawData': 'rawData',                       // Table raw data
      'currentPage': 'currentPage',               // Current page number
      'itemsPerPage': 'itemsPerPage',             // Items per page
      'totalItems': 'totalItems',                 // Total count
      'filterType': 'filterType',                 // Active filter type
      'startDate': 'startDate',                   // Date filter: start
      'endDate': 'endDate',                       // Date filter: end
      'intervalFilter': 'intervalFilter',         // Date interval filter
      'highlightedRowIndex': 'highlightedRowIndex', // Highlighted row
      'isSkeletonLoader': 'isSkeletonLoader',     // Loading state
      'subscriptionCounts': 'subscriptionCounts', // Filter counts
      'noSubscriptionTotal': 'noSubscriptionTotal', // No subscription total
      'loadliveTotal': 'loadliveTotal'            // Load live total
    },
    postRestoration: {
      highlightRow: {
        indexProperty: 'highlightedRowIndex',
        rowSelector: 'tr[data-row-index]',        // DataTable row selector
        highlightClass: 'highlighted-row',
        scrollIntoView: false,
        restorePagination: false // Pagination is stored directly in state
      }
    }
  }),
];

/**
 * Helper function to find route config by path
 * Supports exact match and wildcard matching
 */
export function findRouteConfig(path: string): RouteStateConfig | undefined {
  return PRESERVED_ROUTES.find(config => {
    // Exact match
    if (config.path === path) return true;

    // Custom matcher
    if (config.pathMatcher && config.pathMatcher(path)) return true;

    // Wildcard matching
    if (config.path.includes('*')) {
      const pattern = config.path.replace(/\*/g, '.*');
      const regex = new RegExp(`^${pattern}$`);
      return regex.test(path);
    }

    // Starts with match (for nested routes)
    if (path.startsWith(config.path)) return true;

    return false;
  });
}

/**
 * Check if a route should have state preserved
 */
export function shouldPreserveRoute(path: string): boolean {
  const config = findRouteConfig(path);
  if (!config) return false;

  // Check if preservation is enabled
  if (!config.preserveState) return false;

  // Check custom condition if exists
  if (config.preserveCondition && !config.preserveCondition()) {
    return false;
  }

  return true;
}

/**
 * Get all configured routes sorted by priority
 */
export function getSortedRouteConfigs(): RouteStateConfig[] {
  return [...PRESERVED_ROUTES].sort((a, b) => (b.priority || 0) - (a.priority || 0));
}
